// @ts-nocheck
/**
 * relatedItemBinding.ts — DEC-074 cross-item categoryProperty.itemFrom helpers.
 */

import type { ItemDefinition } from '../../../../../shims/appStub';
import type { CategoryPropertyRef, DataRef, ValueSource } from '../../types/binding';
import type { CompiledRuntimeNode } from '../compiler';
import type { BindingContext } from './types';
import { parseItemVariables, readVariable } from '../variables/itemVariableStorage';

/** Coerce itemVariable / ItemRef-like values to a positive item id. */
export function coerceItemRefIdFromValue(value: unknown): number | null {
  let raw: unknown = value;
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (!('itemId' in record)) {
      return null;
    }
    raw = record.itemId;
  }
  const num = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(num)) {
    return null;
  }
  const truncated = Math.trunc(num);
  if (truncated <= 0) {
    return null;
  }
  return truncated;
}

/** Read the numeric item id stored in an itemVariable on `item` (itemRef or raw number). */
export function readItemRefIdFromItemVariable(
  item: ItemDefinition,
  key: string,
): number | null {
  if (!key?.trim()) return null;
  const bag = parseItemVariables(item.itemVariablesJson ?? '').bag;
  const read = readVariable(bag, key);
  if (!read.found) return null;
  return coerceItemRefIdFromValue(read.value);
}

export function isCategoryPropertyRef(ref: unknown): ref is CategoryPropertyRef {
  return (
    typeof ref === 'object'
    && ref !== null
    && (ref as { kind?: string }).kind === 'categoryProperty'
  );
}

/** Collect unique itemVariable keys used as categoryProperty.itemFrom in a ValueSource tree. */
export function collectItemFromKeysFromValueSource(
  source: ValueSource<unknown> | DataRef | null | undefined,
  out: Set<string>,
): void {
  if (source == null || typeof source !== 'object') return;
  const record = source as Record<string, unknown>;
  if (record.kind === 'binding' && record.ref != null) {
    collectItemFromKeysFromValueSource(record.ref as DataRef, out);
    return;
  }
  if (isCategoryPropertyRef(source) && source.itemFrom?.kind === 'itemVariable') {
    const key = source.itemFrom.key?.trim();
    if (key) out.add(key);
  }
}

/** Walk compiled layout nodes for categoryProperty.itemFrom keys. */
export function collectItemFromKeysFromCompiledNodes(
  nodes: readonly CompiledRuntimeNode[],
): string[] {
  const out = new Set<string>();
  const visitUnknown = (value: unknown): void => {
    if (value == null) return;
    if (Array.isArray(value)) {
      for (const entry of value) visitUnknown(entry);
      return;
    }
    if (typeof value !== 'object') return;
    const record = value as Record<string, unknown>;
    if (record.kind === 'binding' || record.kind === 'categoryProperty') {
      collectItemFromKeysFromValueSource(value as ValueSource<unknown> | DataRef, out);
    }
    for (const nested of Object.values(record)) {
      if (nested && typeof nested === 'object') {
        visitUnknown(nested);
      }
    }
  };
  for (const node of nodes) {
    visitUnknown(node.props);
    visitUnknown(node.interactions);
    visitUnknown((node as { style?: unknown }).style);
    visitUnknown((node as { visibility?: unknown }).visibility);
    visitUnknown((node as { enabled?: unknown }).enabled);
  }
  return [...out];
}

/**
 * Resolve target item ids from itemFrom variable keys on the current binding item.
 */
export function resolveRelatedItemIdsForItemFromKeys(
  item: ItemDefinition,
  keys: readonly string[],
): number[] {
  const ids = new Set<number>();
  for (const key of keys) {
    const id = readItemRefIdFromItemVariable(item, key);
    if (id != null && id !== Number(item.id)) {
      ids.add(id);
    }
  }
  return [...ids];
}

/**
 * Look up a related item for DEC-074 resolution (sync).
 * Prefers relatedItemsById, then script overrides, then parent/children/siblings.
 */
export function lookupRelatedItem(
  context: BindingContext,
  itemId: number,
): ItemDefinition | undefined {
  const fromMap = context.relatedItemsById?.get(itemId);
  if (fromMap) return fromMap;
  const overridden = context.scriptItemOverrides?.get(itemId);
  if (overridden) return overridden;
  if (Number(context.item.id) === itemId) return context.item;
  if (context.parentItem && Number(context.parentItem.id) === itemId) {
    return context.parentItem;
  }
  const fromChildren = context.childItems?.find((child) => Number(child.id) === itemId);
  if (fromChildren) return fromChildren;
  const fromSiblings = context.siblingItems?.find((sib) => Number(sib.id) === itemId);
  if (fromSiblings) return fromSiblings;
  return undefined;
}

export async function prefetchRelatedItemsById(
  itemIds: readonly number[],
  fetchItemById: (itemId: number) => Promise<ItemDefinition | null | undefined>,
): Promise<Map<number, ItemDefinition>> {
  const map = new Map<number, ItemDefinition>();
  await Promise.all(
    itemIds.map(async (id) => {
      const item = await fetchItemById(id);
      if (item) {
        map.set(id, item);
      }
    }),
  );
  return map;
}
